import { useMemo } from 'react';
import { Check, ChevronDown, Filter } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { DatePicker } from '@/shared/components/ui/date-picker';
import { Label } from '@/shared/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { cn } from '@/shared/lib/cn';
import { STATUS_RO_VALUES } from './StatusRoBadge';

/**
 * "Filtrar" button + dropdown that hosts the multi-select status list
 * and the date-range inputs. Search is kept separate at the page level
 * because it's the most-used control and belongs in the main toolbar,
 * not buried inside a dropdown.
 */

export type VentasFilterValue = {
  /** Empty set = no status filter. */
  statuses: string[];
  /** `yyyy-mm-dd` strings, same shape as `<input type="date">` returns. */
  desde: string;
  hasta: string;
};

export const EMPTY_FILTERS: VentasFilterValue = {
  statuses: [],
  desde: '',
  hasta: '',
};

/**
 * Statuses offered in the Estado filter. "Pago Realizado" is excluded
 * because it's a **transient** state — a fully-paid venta moves straight
 * to "Pendiente Despacho", so no row ever sits in it and filtering by it
 * always returned nothing (boss decision 2026-06-17). The status still
 * exists in `STATUS_RO_VALUES` for the badge + lifecycle transitions.
 */
const FILTERABLE_STATUSES = STATUS_RO_VALUES.filter((s) => s !== 'Pago Realizado');

export function VentasFiltersButton({
  value,
  onChange,
}: {
  value: VentasFilterValue;
  onChange: (next: VentasFilterValue) => void;
}) {
  const activeCount = useMemo(
    () =>
      (value.statuses.length > 0 ? 1 : 0) +
      (value.desde ? 1 : 0) +
      (value.hasta ? 1 : 0),
    [value],
  );

  const toggleStatus = (s: string) => {
    const next = value.statuses.includes(s)
      ? value.statuses.filter((x) => x !== s)
      : [...value.statuses, s];
    onChange({ ...value, statuses: next });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-12 gap-2 px-4"
          aria-label="Abrir filtros"
        >
          <Filter className="h-4 w-4" aria-hidden />
          Filtrar
          {activeCount > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
              {activeCount}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Estado</Label>
              {value.statuses.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange({ ...value, statuses: [] })}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                  Limpiar
                </button>
              )}
            </div>
            <div className="max-h-48 overflow-y-auto rounded-lg border bg-background p-1">
              {FILTERABLE_STATUSES.map((s) => {
                const checked = value.statuses.includes(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => toggleStatus(s)}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none',
                      'hover:bg-secondary focus-visible:bg-secondary',
                      checked && 'font-medium',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        checked
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-input bg-background',
                      )}
                      aria-hidden
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Rango de fecha de pago</Label>
            <div className="flex items-center gap-2">
              <DatePicker
                value={value.desde}
                max={value.hasta || undefined}
                placeholder="Desde"
                ariaLabel="Desde"
                className="flex-1"
                onChange={(v) => onChange({ ...value, desde: v })}
              />
              <span className="text-sm text-muted-foreground">a</span>
              <DatePicker
                value={value.hasta}
                min={value.desde || undefined}
                placeholder="Hasta"
                ariaLabel="Hasta"
                className="flex-1"
                onChange={(v) => onChange({ ...value, hasta: v })}
              />
            </div>
          </div>

          {activeCount > 0 && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => onChange(EMPTY_FILTERS)}
            >
              Limpiar todos los filtros
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
